'use client';

import { Button, Drawer as DrawerSparky, Icon, Logo, TextLink } from '@petsmart-ui/sparky';
import { useEffect, useState } from 'react';

import type { Entry } from 'contentful';
import type { HeaderDrawerProps } from './types';

import { useCategoryStack } from '../../hooks';
import { DrawerMenuItem } from '../HeaderDrawerItem';
import { SPECIAL_TYPES } from '../MainMenu/types';
import { getOptiHeaderText } from '../../utils/getOptiHeaderText';
import { useUserSession, useCart } from '@libs/common-next';
import { logoutUser, Locale, getLocale } from '@libs/core';

import './HeaderDrawer.scss';

export function HeaderDrawer({ mainMenu, secondaryNavigation }: HeaderDrawerProps) {
    const [isActive, setisActive] = useState(false);
    const { storageUserSession, user } = useUserSession();
    const { firstName, isRegistered } = storageUserSession ?? {};
    const [displayLoggedIn, setDisplayLoggedIn] = useState<boolean | undefined>(undefined);
    const { clearCart } = useCart();
    const isLocaleUs = getLocale() === Locale['en-US'];

    const { handleBackClick, handleCategoryClick, categoryStack, currentCategories } = useCategoryStack(
        mainMenu?.fields?.category ? (mainMenu.fields.category as Entry[]) : []
    );
    useEffect(() => {
        setDisplayLoggedIn(!!user);
    }, [firstName, isRegistered, user]);
    const shopAllBrands = 'Shop all Brands';

    const signUpText = 'Sign up or create account';
    const signLink = '/account/';
    if ((currentCategories.type === 'text' || currentCategories.type === undefined) && currentCategories.displayText) {
        currentCategories.categories?.unshift({
            sysId: '2E8kDFkVWdP14Ge3cgZItl',
            displayText: currentCategories.displayText?.toLowerCase().includes('services')
                ? 'See all ' + currentCategories.displayText
                : 'Shop all ' + getOptiHeaderText(currentCategories.displayText ?? ''),
            link: currentCategories.link,
            type: 'text',
            shopAllCategory: true,
        });
    }

    return (
        <>
            {/* Reimplement menu button */}
            <Button
                data-testid="menu-button"
                className="sparky-c-header__menu-button"
                variant="bare"
                text={isActive ? 'Close' : 'Menu'}
                iconPosition="before"
                iconName={isActive ? 'x' : 'menu'}
                size="lg"
                hideText
                onClick={() => setisActive(true)}
            />

            <DrawerSparky
                className="drawer"
                isActive={isActive}
                onClose={() => setisActive(false)}
                data-testid="header-drawer"
            >
                <DrawerSparky.Header>
                    {categoryStack.length > 1 ? (
                        <div className="header-drawer header-drawer--top">
                            <button
                                data-testid="back-button"
                                onClick={handleBackClick}
                                className="category__button--back category__button"
                                type="button"
                            >
                                <Icon size="lg" name="chevron-left" />
                                <span>{getOptiHeaderText(currentCategories.displayText ?? '')}</span>
                            </button>
                        </div>
                    ) : (
                        <Logo />
                    )}
                </DrawerSparky.Header>
                {currentCategories.categories && (
                    <DrawerSparky.Body className="drawer-body">
                        {currentCategories.specialType === SPECIAL_TYPES.SHOP_BY_BRAND && (
                            <TextLink
                                href={currentCategories.link}
                                title={shopAllBrands}
                                id={currentCategories.displayText}
                                className="shop-all-brand"
                            >
                                {shopAllBrands}
                            </TextLink>
                        )}
                        <ul
                            data-testid="drawer-body"
                            className={`menu-category menu-category--lvl${categoryStack.length}`}
                        >
                            {currentCategories.categories
                                // NOTE: Remove duplicate categories to avoid phantom menu items on back button click
                                // TODO: should be remove if we can get the category stack to work properly with unique IDs
                                .filter(
                                    (category, index, self) => index === self.findIndex(c => c.sysId === category.sysId)
                                )
                                .map(category => (
                                    <DrawerMenuItem
                                        key={category.sysId}
                                        categoryStack={categoryStack}
                                        category={category}
                                        handleCategoryClick={handleCategoryClick}
                                        closeMenu={() => setisActive(false)}
                                    />
                                ))}
                        </ul>
                        {categoryStack.length < 2 && (
                            <div className="secondary-nav">
                                <TextLink href="/treats-rewards">
                                    Treats Rewards
                                    <Icon name="treats-fill" size="sm" />
                                </TextLink>
                                <TextLink href="/gift-cards/">Gift Cards</TextLink>
                                <TextLink href="/learning-center/">Learning Center</TextLink>
                                <TextLink
                                    href={
                                        isLocaleUs ? 'https://petsmartcharities.org/' : 'https://petsmartcharities.ca/'
                                    }
                                >
                                    PetSmart Charities
                                </TextLink>
                                <TextLink href="/featured-shops/auto-ship">Autoship</TextLink>
                                {displayLoggedIn !== undefined && displayLoggedIn ? (
                                    <TextLink onClick={async () => await logoutUser('/account', clearCart)}>
                                        {`Not ${user?.gn}?`}
                                        <span className="sign-up">Sign Out</span>
                                    </TextLink>
                                ) : (
                                    <TextLink data-testid="login" href={signLink} className="login-anchor">
                                        {signUpText}
                                    </TextLink>
                                )}
                            </div>
                        )}
                    </DrawerSparky.Body>
                )}
            </DrawerSparky>
        </>
    );
}
